import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Markdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import useApi from '../api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ExternalLink, FileText, Link2, Loader2 } from 'lucide-react';
import { markdownRenderers } from '../utils/markdownRenderers';
import { JobArtifact, ServiceStatusBadge, apiErrorMessage, formatRelativeTime } from '../services/servicesShared';

const formatSize = (bytes?: number | null): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Artifact viewer. Markdown renders with the dashboard's renderers; HTML
 * renders in a fully sandboxed iframe (sandbox="" — no scripts, no same-
 * origin, no forms) so an AI-built page can never reach our origin
 * (OWNED_JOBS_PLAN.md §1 Artifacts).
 */
const ArtifactDialog: React.FC<{ jobId: string; artifact: JobArtifact | null; onClose: () => void }> = ({ jobId, artifact, onClose }) => {
    const { get } = useApi();
    const [body, setBody] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!artifact) return;
        setBody(null);
        if (artifact.kind === 'link' || artifact.kind === 'file') return;
        let cancelled = false;
        setLoading(true);
        get(`/jobs/${jobId}/artifacts/${artifact.id}`)
            .then((response) => {
                if (!cancelled) setBody(response.data?.body ?? '');
            })
            .catch((error) => toast.error(apiErrorMessage(error, 'Could not open the artifact')))
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [artifact?.id]);

    return (
        <Dialog open={artifact !== null} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {artifact?.title}
                        {artifact?.version ? <span className="text-xs text-muted-foreground font-normal">v{artifact.version}</span> : null}
                    </DialogTitle>
                    <DialogDescription>
                        {artifact?.kind} · {formatRelativeTime(artifact?.created_at)}
                        {artifact?.size_bytes ? ` · ${formatSize(artifact.size_bytes)}` : ''}
                    </DialogDescription>
                </DialogHeader>
                <div className="min-h-0 grow overflow-auto scrollbar-thin">
                    {loading && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground p-4">
                            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                        </div>
                    )}
                    {!loading && artifact?.kind === 'markdown' && body !== null && (
                        <div className="prose prose-sm dark:prose-invert max-w-none p-1">
                            <Markdown remarkPlugins={[remarkGfm, remarkBreaks]} components={markdownRenderers as any}>
                                {body}
                            </Markdown>
                        </div>
                    )}
                    {!loading && artifact?.kind === 'html' && body !== null && (
                        <iframe
                            title={artifact.title}
                            sandbox=""
                            referrerPolicy="no-referrer"
                            srcDoc={body}
                            className="w-full h-[70vh] rounded border bg-white"
                        />
                    )}
                    {(artifact?.kind === 'link' || artifact?.kind === 'file') && (
                        <div className="p-4 text-sm">
                            {artifact.url ? (
                                <a className="inline-flex items-center gap-1 underline" href={artifact.url} target="_blank" rel="noreferrer noopener">
                                    Open {artifact.kind === 'file' ? 'file' : 'link'} <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                            ) : (
                                <span className="text-muted-foreground">No URL recorded for this artifact.</span>
                            )}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export const JobArtifacts: React.FC<{ jobId: string; artifacts: JobArtifact[] }> = ({ jobId, artifacts }) => {
    const [open, setOpen] = useState<JobArtifact | null>(null);
    return (
        <div>
            <h3 className="text-lg font-semibold mb-3">Artifacts</h3>
            {artifacts.length ? (
                <div className="rounded-lg border divide-y">
                    {artifacts.map((artifact) => (
                        <div key={artifact.id} className="flex items-center justify-between gap-3 px-3 py-2">
                            <div className="flex items-center gap-2 min-w-0">
                                {artifact.kind === 'link' ? (
                                    <Link2 className="h-4 w-4 text-muted-foreground shrink-0" />
                                ) : (
                                    <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                                )}
                                <div className="min-w-0">
                                    <div className="text-sm font-medium truncate">{artifact.title}</div>
                                    <div className="text-xs text-muted-foreground truncate">
                                        {artifact.kind}
                                        {artifact.version ? ` · v${artifact.version}` : ''} · {formatRelativeTime(artifact.created_at)}
                                        {artifact.size_bytes ? ` · ${formatSize(artifact.size_bytes)}` : ''}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                {artifact.visibility && <ServiceStatusBadge status={artifact.visibility} />}
                                <Button variant="outline" size="sm" onClick={() => setOpen(artifact)}>
                                    Open
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-sm text-muted-foreground">No artifacts published yet.</p>
            )}
            <ArtifactDialog jobId={jobId} artifact={open} onClose={() => setOpen(null)} />
        </div>
    );
};

export default JobArtifacts;
